import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getComicById, getComicsByCategory, Comic, categories } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | undefined>();
  const [relatedComics, setRelatedComics] = useState<Comic[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (id) {
      const found = getComicById(Number(id));
      setComic(found);
      if (found) {
        const related = getComicsByCategory(found.categorySlug)
          .filter((c) => c.id !== found.id)
          .slice(0, 4);
        setRelatedComics(related);
      }
    }
  }, [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 mb-4">漫画未找到</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: `${comic.title} - 星球漫画`,
          description: comic.description,
          author: { '@type': 'Person', name: comic.author },
          datePublished: comic.chapters[comic.chapters.length - 1]?.updateDate,
          dateModified: comic.updateDate,
          image: comic.coverImage,
        }}
      />

      <Navbar />
      <main className="pt-16">
        <article className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          <div className="mt-6 flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0">
              <div className="w-60 rounded-lg overflow-hidden">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 星球漫画`}
                  title={comic.title}
                  className="w-full aspect-[3/4] object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className={`text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-amber-400"></i>
                  </div>
                  <strong>{comic.rating}</strong> 分
                </span>
                <span className="text-xs text-foreground-400">
                  <div className="w-3.5 h-3.5 inline-flex items-center justify-center mr-1">
                    <i className="ri-eye-line"></i>
                  </div>
                  {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次阅读
                </span>
                <span className="text-xs text-foreground-400">
                  <div className="w-3.5 h-3.5 inline-flex items-center justify-center mr-1">
                    <i className="ri-heart-line"></i>
                  </div>
                  {comic.favorites > 10000 ? `${(comic.favorites / 10000).toFixed(1)}万` : comic.favorites} 收藏
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-600 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-4 mb-6">
                <h2 className="font-heading text-base font-semibold text-foreground-950 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.longDescription}</p>
              </div>

              <Link
                to={`/comic/${comic.id}`}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md font-medium text-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-circle-line"></i>
                </div>
                开始阅读
              </Link>
            </div>
          </div>

          <section className="mt-12">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5">
              <a href="#chapters" className="cursor-pointer">章节列表</a>
            </h2>
            <div className="bg-background-50 rounded-lg border border-background-200/70">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 p-2">
                {comic.chapters.map((chapter) => (
                  <Link
                    key={chapter.id}
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-between p-3 rounded hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="text-sm text-foreground-700 truncate">{chapter.title}</span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{chapter.updateDate}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="mt-14">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-foreground-950">
                  <a href="#related" className="cursor-pointer">相关推荐</a>
                </h2>
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap cursor-pointer"
                >
                  查看更多
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-product-shop>
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}